import { useCallback, useLayoutEffect, useState } from 'react';

import { OperateEnum } from './Operate';

function trim(value: string, elem: unknown) {
  if (elem instanceof HTMLInputElement) {
    return value.trim();
  }

  if (elem instanceof HTMLTextAreaElement) {
    return value
      .replace(/\n+\s*\n+/g, '\n')
      .replace(/^\s*$/, '')
      .replace(/^\n/, '')
      .replace(/\n$/, '');
  }

  return value;
}

export function useInitInput<E>({
  inputRef,
  value: dbValue,
  required = true,
  disabled = false,
  onChange,
  setError,
}: {
  inputRef?: React.RefObject<E>;
  value: string;
  required?: Boolean;
  disabled?: Boolean;
  onChange: (value: string, oldValue?: string) => void;
  setError?: (error: boolean) => void;
}) {
  const [data, setData] = useState('');
  const [oldData, setOldData] = useState('');
  const [isEditable, setIsEditable] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [disabledOk, setDisabledOk] = useState(false);

  const scrollToTop = useCallback(() => {
    const input = inputRef?.current;
    if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
      setTimeout(() => {
        input.scrollTo?.(0, 0);
      });
    }
  }, [inputRef]);

  const handleFocus = useCallback(() => {
    if (disabled || isEditable) return;

    setIsEditable(true);

    const input = inputRef?.current;
    if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
      setTimeout(() => {
        input.focus();
        input.scrollTo?.(999999, 999999);

        const disabled = required && !dbValue.trim();
        setDisabledCancel(disabled);
        setDisabledOk(disabled);
      });

      setTimeout(() => {
        input.setSelectionRange(999999, 999999);
      }, 100);
    }
  }, [dbValue, disabled, inputRef, isEditable, required]);

  const handleBlur = useCallback(
    (event: React.FocusEvent<E>) => {
      if (event.relatedTarget instanceof HTMLDivElement && event.relatedTarget.dataset.type) return;

      const targetVal = trim(data, inputRef?.current) || (required ? oldData : '');

      if (!required || targetVal) {
        setData(targetVal);
        targetVal !== oldData && onChange(targetVal, oldData);
        setError?.(false);
      }

      scrollToTop();
      setIsEditable(false);
    },
    [data, inputRef, oldData, onChange, required, scrollToTop, setError],
  );

  const handleOperate = useCallback(
    (type: OperateEnum) => {
      const isOk = type === OperateEnum.OK;
      const targetVal = trim(isOk ? data : oldData, inputRef?.current);

      if (!required || targetVal) {
        setData(targetVal);
        scrollToTop();
        setIsEditable(false);

        if (isOk) {
          targetVal !== oldData && onChange(targetVal, oldData);
        } else {
          setError?.(false);
        }
      } else if (inputRef?.current instanceof HTMLInputElement || inputRef?.current instanceof HTMLTextAreaElement) {
        inputRef.current.focus();
      }
    },
    [data, inputRef, oldData, onChange, required, scrollToTop, setError],
  );

  const handleChange = useCallback(
    (value: string) => {
      setData(value);
      setDisabledCancel(required && !dbValue.trim());
      const isValid = required ? !!value.trim() : true;
      setDisabledOk(!isValid);
      setError?.(!isValid);
    },
    [dbValue, required, setError],
  );

  useLayoutEffect(() => {
    setData(dbValue);
    setOldData(dbValue);
  }, [dbValue]);

  return {
    data,
    isEditable,
    disabledCancel,
    disabledOk,
    handleFocus,
    handleBlur,
    handleOperate,
    handleChange,
  };
}
